/**
 * Users Domain Transformers
 * API ↔ Domain mapping (Anti-Corruption Layer)
 * Converts between Strapi user model and domain User type
 */

import type {
  User,
  UserStats,
  UsersFilter,
  CreateUserRequest,
  UserRole,
  UserStatus,
  StrapiRoleType,
  TenantRef,
  RoleRef,
} from '../domain/types'
import type {
  UserDto,
  StrapiUserListResponse,
  CreateUserRequestDto,
  UpdateUserRequestDto,
  StrapiRoleDto,
  TenantRefDto,
} from './api-types'

// ===========================================
// Role Mapping
// ===========================================

// Map Strapi role type to UI-friendly role
function mapStrapiRoleToUserRole(roleType: StrapiRoleType | string): UserRole {
  const roleMap: Record<string, UserRole> = {
    super_admin: 'admin',
    admin: 'admin',
    finance_admin: 'admin',
    tenant_admin: 'admin',
    tenant_user: 'manager',
    sales_office_user: 'manager',
    security_guard: 'operator',
    operator: 'operator',
    kiosk_user: 'operator',
    employee: 'operator',
    customer: 'operator',
    customer_operator: 'operator',
    authenticated: 'viewer',
    public: 'viewer',
    ecommerce: 'viewer',
  }
  return roleMap[roleType] ?? 'viewer'
}

// Map UI role to Strapi role type (for filtering)
function mapUserRoleToStrapiRoleType(role: UserRole): StrapiRoleType[] {
  const roleMap: Record<UserRole, StrapiRoleType[]> = {
    admin: ['super_admin', 'tenant_admin'],
    manager: ['tenant_user'],
    operator: ['customer', 'customer_operator'],
    viewer: ['authenticated', 'public', 'ecommerce'],
  }
  return roleMap[role] ?? ['authenticated']
}

// ===========================================
// Status Mapping
// ===========================================

function deriveUserStatus(dto: UserDto): UserStatus {
  if (dto.blocked) return 'blocked'
  if (dto.userStatus === 'inactive') return 'inactive'
  if (dto.userStatus === 'invited') return 'invited'
  if (dto.userStatus === 'active') return 'active'
  return 'active'
}

// ===========================================
// API → Domain Transformers
// ===========================================

export function transformRoleDtoToRef(dto: StrapiRoleDto | null | undefined): RoleRef | undefined {
  if (!dto) return undefined
  return {
    id: dto.id,
    name: dto.name,
    type: dto.type as StrapiRoleType,
  }
}

export function transformTenantDtoToRef(dto: TenantRefDto | null | undefined): TenantRef | null {
  if (!dto) return null
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
  }
}

export function transformUserDtoToDomain(dto: UserDto): User {
  const roleType = dto.role?.type ?? 'authenticated'

  return {
    id: String(dto.id),
    documentId: dto.documentId,
    username: dto.username,
    name: dto.fullName ?? dto.email.split('@')[0],
    email: dto.email,
    phone: dto.phone ?? undefined,
    companyName: dto.companyName ?? undefined,
    role: mapStrapiRoleToUserRole(roleType),
    strapiRole: transformRoleDtoToRef(dto.role),
    status: deriveUserStatus(dto),
    permissions: [], // Permissions not stored on user in Strapi
    lastActive: dto.lastLoginAt ?? undefined,
    createdAt: dto.createdAt,
    confirmed: dto.confirmed,
    blocked: dto.blocked,
    attemptBlocked: dto.attemptBlocked,
    tenant: transformTenantDtoToRef(dto.tenant),
    factoryId: dto.factory ? String(dto.factory.id) : undefined,
    factoryDocumentId: dto.factory?.documentId ?? undefined,
    locationId: dto.location ? String(dto.location.id) : undefined,
    locationDocumentId: dto.location?.documentId ?? undefined,
  }
}

export function transformUsersDtosToDomain(dtos: UserDto[]): User[] {
  return dtos.map(transformUserDtoToDomain)
}

export function transformUserListResponse(response: StrapiUserListResponse): User[] {
  return transformUsersDtosToDomain(response.data ?? response)
}

// ===========================================
// Domain → API Transformers
// ===========================================

// Role type to Role ID mapping (these should match your Strapi roles)
const ROLE_TYPE_TO_ID: Record<string, number> = {
  super_admin: 1,
  tenant_admin: 2,
  tenant_user: 3,
  customer: 4,
  authenticated: 5,
  public: 6,
  ecommerce: 7,
}

export function transformCreateUserToRequest(
  data: CreateUserRequest,
  roleId?: number
): CreateUserRequestDto {
  // Map domain role to Strapi role ID
  const roleIdValue = roleId ?? ROLE_TYPE_TO_ID.tenant_user

  return {
    email: data.email,
    username: data.email, // Use email as username
    password: data.password,
    fullName: data.name,
    phone: data.phone,
    role: roleIdValue,
    tenant: data.tenantId ? Number(data.tenantId) : undefined,
    factory: data.factoryId ? Number(data.factoryId) : undefined,
    location: data.locationId ? Number(data.locationId) : undefined,
    confirmed: false, // New users need to confirm
    blocked: false,
  }
}

export function transformUpdateUserToRequest(
  data: Partial<Pick<User, 'name' | 'phone'>> & {
    blocked?: boolean
    confirmed?: boolean
    userStatus?: 'active' | 'inactive' | 'invited'
    role?: number
    factory?: number | null
    location?: number | null
  }
): UpdateUserRequestDto {
  const result: UpdateUserRequestDto = {}

  if (data.name !== undefined) result.fullName = data.name
  if (data.phone !== undefined) result.phone = data.phone
  if (data.role !== undefined) result.role = data.role
  if (data.factory !== undefined) result.factory = data.factory
  if (data.location !== undefined) result.location = data.location
  if (data.blocked !== undefined) result.blocked = data.blocked
  if (data.confirmed !== undefined) result.confirmed = data.confirmed
  if (data.userStatus !== undefined) result.userStatus = data.userStatus

  return result
}

// ===========================================
// Filter → Query Transformers
// ===========================================

export function transformFilterToQuery(filter: UsersFilter): Record<string, unknown> {
  const params: Record<string, unknown> = {
    'populate[role]': true,
    'populate[tenant]': true,
    'sort': 'createdAt:desc',
  }

  // Pagination
  if (filter.page) {
    params['pagination[page]'] = filter.page
  }
  if (filter.pageSize) {
    params['pagination[pageSize]'] = filter.pageSize
  }

  // Status filter
  if (filter.status && filter.status !== 'all') {
    switch (filter.status) {
      case 'blocked':
        params['filters[blocked][$eq]'] = true
        break
      case 'invited':
        params['filters[userStatus][$eq]'] = 'invited'
        params['filters[blocked][$eq]'] = false
        break
      case 'active':
        params['filters[userStatus][$eq]'] = 'active'
        params['filters[blocked][$eq]'] = false
        break
      case 'inactive':
        params['filters[userStatus][$eq]'] = 'inactive'
        params['filters[blocked][$eq]'] = false
        break
    }
  }

  // Role filter (filter by Strapi role type)
  if (filter.role && filter.role !== 'all') {
    params['filters[role][type][$eq]'] = filter.role
  }

  // Tenant filter
  if (filter.tenantId && filter.tenantId !== 'all') {
    params['filters[tenant][id][$eq]'] = Number(filter.tenantId)
  }

  // Search filter (search by name or email)
  if (filter.search) {
    params['filters[$or][0][fullName][$containsi]'] = filter.search
    params['filters[$or][1][email][$containsi]'] = filter.search
  }

  return params
}

// ===========================================
// Stats Calculation (computed from user list)
// ===========================================

export function calculateUserStats(users: User[]): UserStats {
  const stats: UserStats = {
    active: 0,
    inactive: 0,
    invited: 0,
    blocked: 0,
    total: users.length,
    byRole: {},
  }

  for (const user of users) {
    // Count by status
    switch (user.status) {
      case 'active':
        stats.active++
        break
      case 'inactive':
        stats.inactive++
        break
      case 'invited':
        stats.invited++
        break
      case 'blocked':
        stats.blocked++
        break
    }

    // Count by role
    const roleType = user.strapiRole?.type ?? 'unknown'
    stats.byRole[roleType] = (stats.byRole[roleType] ?? 0) + 1
  }

  return stats
}
